/**
 * Limited Cart Offer
 *
 * Fuente visual principal:
 * - hs_limited_cart_offer_expires_at
 * - hs_limited_cart_offer_claimed
 *
 * El carrito se consulta solamente para sincronizar y validar.
 * Cada vez que el tema reemplaza el cart drawer, MutationObserver
 * vuelve a aplicar el estado directamente desde localStorage.
 */

(function (window, document) {
  'use strict';

  class LimitedCartOfferController {
    constructor() {
      this.defaults = {
        durationMinutes: 30,
        rootSelector: '[data-limited-offer]',
        buttonSelector: '[data-limited-offer-apply]',
        buttonLabelSelector: '[data-limited-offer-button-label]',
        timerSelector: '[data-limited-offer-timer]',
        timerContainerSelector:
          '[data-limited-offer-timer-container]',
        availableContentSelector:
          '[data-limited-offer-available]',
        claimedContentSelector:
          '[data-limited-offer-claimed]',
        expiredSelector:
          '[data-limited-offer-expired]',

        claimedAttribute:
          '_hs_limited_offer_claimed',

        storageKey:
          'hs_limited_cart_offer_expires_at',

        claimedStorageKey:
          'hs_limited_cart_offer_claimed',

        requestTimeout: 15000,

        disableButtonWhenClaimed: true,

        texts: {
          defaultButton:
            'Apply 10% Savings',
          loadingButton:
            'Applying...',
          claimedButton:
            '10% Savings Applied',
          expiredButton:
            'Offer Expired',
          errorButton:
            'Try Again'
        },

        onCartUpdated: null,
        onClaimed: null,
        onExpired: null,
        onRemoved: null,
        onRestored: null,
        onError: null
      };

      this.config =
        this.mergeConfig({});

      this.countdownId = null;
      this.expirationTimestamp = null;
      this.requestInProgress = false;
      this.expirationInProgress = false;
      this.initialized = false;
      this.claimed = false;

      this.observer = null;
      this.mutationFrame = null;

      this.handleClick =
        this.handleClick.bind(this);

      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);

      this.handleSectionLoad =
        this.handleSectionLoad.bind(this);

      this.handleMutations =
        this.handleMutations.bind(this);
    }

    mergeConfig(options) {
      return {
        ...this.defaults,
        ...options,
        texts: {
          ...this.defaults.texts,
          ...(options.texts || {})
        }
      };
    }

    queryAll(selector, context) {
      return Array.from(
        (context || document)
          .querySelectorAll(selector)
      );
    }

    getRoots(context) {
      return this.queryAll(
        this.config.rootSelector,
        context
      );
    }

    getButtons(context) {
      return this.queryAll(
        this.config.buttonSelector,
        context
      );
    }

    getTimers(context) {
      return this.queryAll(
        this.config.timerSelector,
        context
      );
    }

    getRootUrl() {
      const root =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      return root.endsWith('/')
        ? root
        : `${root}/`;
    }

    getCartUrl(type) {
      const routes = {
        get: 'cart.js',
        update: 'cart/update.js'
      };

      if (!routes[type]) {
        throw new Error(
          `[LimitedCartOffer] Unsupported cart route: ${type}`
        );
      }

      return `${this.getRootUrl()}${routes[type]}`;
    }

    createAbortRequest() {
      if (
        typeof AbortController ===
        'undefined'
      ) {
        return {
          signal: undefined,
          clear: function () {}
        };
      }

      const controller =
        new AbortController();

      const timeoutId =
        window.setTimeout(() => {
          controller.abort();
        }, this.config.requestTimeout);

      return {
        signal: controller.signal,

        clear: function () {
          window.clearTimeout(
            timeoutId
          );
        }
      };
    }

    async parseError(
      response,
      fallbackMessage
    ) {
      try {
        const data =
          await response.json();

        return (
          data.description ||
          data.message ||
          data.error ||
          fallbackMessage
        );
      } catch (error) {
        return fallbackMessage;
      }
    }

    async fetchCart() {
      const request =
        this.createAbortRequest();

      try {
        const response = await fetch(
          `${this.getCartUrl('get')}?limited_offer=${Date.now()}`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json',
              'Cache-Control':
                'no-cache'
            },

            credentials:
              'same-origin',

            cache:
              'no-store',

            signal:
              request.signal
          }
        );

        if (!response.ok) {
          const message =
            await this.parseError(
              response,
              `Unable to retrieve cart: ${response.status}`
            );

          throw new Error(message);
        }

        return await response.json();
      } finally {
        request.clear();
      }
    }

    async updateCartAttributes(
      attributes
    ) {
      if (
        !attributes ||
        typeof attributes !==
          'object'
      ) {
        throw new TypeError(
          '[LimitedCartOffer] attributes must be an object.'
        );
      }

      const request =
        this.createAbortRequest();

      try {
        const response = await fetch(
          this.getCartUrl('update'),
          {
            method: 'POST',

            headers: {
              Accept:
                'application/json',

              'Content-Type':
                'application/json'
            },

            credentials:
              'same-origin',

            cache:
              'no-store',

            body: JSON.stringify({
              attributes
            }),

            signal:
              request.signal
          }
        );

        if (!response.ok) {
          const message =
            await this.parseError(
              response,
              `Unable to update cart: ${response.status}`
            );

          throw new Error(message);
        }

        const cart =
          await response.json();

        this.emit(
          'limited-offer:cart-updated',
          {
            cart,
            attributes
          }
        );

        if (
          typeof this.config
            .onCartUpdated ===
          'function'
        ) {
          await this.config
            .onCartUpdated(
              cart,
              attributes
            );
        }

        HsCartDrawer.getSlideCart(
          HsCartDrawer.globalData,
          HsCartDrawer.query
        );

        return cart;
      } finally {
        request.clear();
      }
    }

    readTimestamp(key) {
      try {
        const value =
          window.localStorage
            .getItem(key);

        if (!value) {
          return null;
        }

        const timestamp =
          Number(value);

        if (
          !Number.isFinite(
            timestamp
          )
        ) {
          window.localStorage
            .removeItem(key);

          return null;
        }

        return timestamp;
      } catch (error) {
        this.handleError(error);
        return null;
      }
    }

    writeTimestamp(
      key,
      timestamp
    ) {
      try {
        window.localStorage.setItem(
          key,
          String(timestamp)
        );
      } catch (error) {
        this.handleError(error);
      }
    }

    removeStorageKey(key) {
      try {
        window.localStorage
          .removeItem(key);
      } catch (error) {
        this.handleError(error);
      }
    }

    getStoredExpiration() {
      return this.readTimestamp(
        this.config.storageKey
      );
    }

    saveExpiration(timestamp) {
      this.writeTimestamp(
        this.config.storageKey,
        timestamp
      );
    }

    clearStoredExpiration() {
      this.removeStorageKey(
        this.config.storageKey
      );
    }

    getStoredClaimedExpiration() {
      const timestamp =
        this.readTimestamp(
          this.config
            .claimedStorageKey
        );

      if (
        !Number.isFinite(
          timestamp
        )
      ) {
        return null;
      }

      if (
        timestamp <= Date.now()
      ) {
        this
          .clearStoredClaimedExpiration();

        return null;
      }

      return timestamp;
    }

    saveStoredClaimedExpiration(
      timestamp
    ) {
      this.writeTimestamp(
        this.config
          .claimedStorageKey,
        timestamp
      );
    }

    clearStoredClaimedExpiration() {
      this.removeStorageKey(
        this.config
          .claimedStorageKey
      );
    }

    createExpiration() {
      const duration =
        Number(
          this.config
            .durationMinutes
        ) *
        60 *
        1000;

      if (
        !Number.isFinite(
          duration
        ) ||
        duration <= 0
      ) {
        throw new Error(
          '[LimitedCartOffer] durationMinutes must be greater than zero.'
        );
      }

      const expiration =
        Date.now() + duration;

      this.saveExpiration(
        expiration
      );

      return expiration;
    }

    getClaimedExpiration(cart) {
      const attributes =
        cart &&
        cart.attributes &&
        typeof cart.attributes ===
          'object'
          ? cart.attributes
          : {};

      const value =
        attributes[
          this.config
            .claimedAttribute
        ];

      if (!value) {
        return null;
      }

      const timestamp =
        Date.parse(value);

      return Number.isFinite(
        timestamp
      )
        ? timestamp
        : null;
    }

    formatTime(milliseconds) {
      const totalSeconds =
        Math.max(
          0,
          Math.ceil(
            milliseconds / 1000
          )
        );

      const hours =
        Math.floor(
          totalSeconds / 3600
        );

      const minutes =
        Math.floor(
          (totalSeconds % 3600) /
            60
        );

      const seconds =
        totalSeconds % 60;

      return (
        `Hrs: ${String(hours)
          .padStart(2, '0')}` +
        `  Min: ${String(minutes)
          .padStart(2, '0')}` +
        `  Sec: ${String(seconds)
          .padStart(2, '0')}`
      );
    }

    setTimerValue(
      milliseconds,
      roots = null
    ) {
      const remaining =
        Math.max(
          0,
          milliseconds
        );

      const formatted =
        this.formatTime(
          remaining
        );

      const timers =
        roots
          ? roots.flatMap(
              (root) =>
                this.getTimers(root)
            )
          : this.getTimers();

      timers.forEach(
        function (timer) {
          timer.textContent =
            formatted;

          timer.setAttribute(
            'data-limited-offer-remaining',
            String(remaining)
          );

          timer.setAttribute(
            'aria-label',
            formatted
          );
        }
      );
    }

    updateCountdownDisplay(
      roots = null
    ) {
      if (
        !Number.isFinite(
          this
            .expirationTimestamp
        )
      ) {
        return 0;
      }

      const remaining =
        Math.max(
          0,
          this
            .expirationTimestamp -
            Date.now()
        );

      this.setTimerValue(
        remaining,
        roots
      );

      return remaining;
    }

    setButtonLabel(
      button,
      text
    ) {
      const label =
        button.querySelector(
          this.config
            .buttonLabelSelector
        );

      if (label) {
        label.textContent =
          text;
      } else {
        button.textContent =
          text;
      }
    }

    setButtonState(
      state,
      roots = null
    ) {
      const buttons =
        roots
          ? roots.flatMap(
              (root) =>
                this.getButtons(root)
            )
          : this.getButtons();

      buttons.forEach(
        (button) => {
          button.removeAttribute(
            'aria-busy'
          );

          button.removeAttribute(
            'disabled'
          );

          button.removeAttribute(
            'aria-disabled'
          );

          button.setAttribute(
            'data-limited-offer-button-state',
            state
          );

          switch (state) {
            case 'loading':
              this.setButtonLabel(
                button,
                this.config.texts
                  .loadingButton
              );

              button.setAttribute(
                'aria-busy',
                'true'
              );

              button.setAttribute(
                'disabled',
                'disabled'
              );
              break;

            case 'claimed':
              this.setButtonLabel(
                button,
                this.config.texts
                  .claimedButton
              );

              button.setAttribute(
                'aria-disabled',
                'true'
              );

              if (
                this.config
                  .disableButtonWhenClaimed
              ) {
                button.setAttribute(
                  'disabled',
                  'disabled'
                );
              }
              break;

            case 'expired':
              this.setButtonLabel(
                button,
                this.config.texts
                  .expiredButton
              );

              button.setAttribute(
                'disabled',
                'disabled'
              );
              break;

            case 'error':
              this.setButtonLabel(
                button,
                this.config.texts
                  .errorButton
              );
              break;

            default:
              this.setButtonLabel(
                button,
                this.config.texts
                  .defaultButton
              );
              break;
          }
        }
      );
    }

    setOfferState(
      state,
      roots = null
    ) {
      const targetRoots =
        roots || this.getRoots();

      targetRoots.forEach(
        (root) => {
          const available =
            root.querySelector(
              this.config
                .availableContentSelector
            );

          const claimed =
            root.querySelector(
              this.config
                .claimedContentSelector
            );

          const expired =
            root.querySelector(
              this.config
                .expiredSelector
            );

          const timerContainer =
            root.querySelector(
              this.config
                .timerContainerSelector
            );

          const button =
            root.querySelector(
              this.config
                .buttonSelector
            );

          root.hidden = false;

          root.setAttribute(
            'data-offer-state',
            state
          );

          if (available) {
            available.hidden =
              state !==
              'available';
          }

          if (claimed) {
            claimed.hidden =
              state !==
              'claimed';
          }

          if (expired) {
            expired.hidden =
              state !==
              'expired';
          }

          if (timerContainer) {
            timerContainer.hidden =
              state ===
              'expired';
          }

          if (button) {
            button.hidden =
              state !==
              'available';
          }
        }
      );
    }

    emit(name, detail) {
      document.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: detail || {}
        })
      );
    }

    stopCountdown() {
      if (
        this.countdownId !==
        null
      ) {
        window.clearInterval(
          this.countdownId
        );

        this.countdownId =
          null;
      }
    }

    startCountdown(
      expiresAt
    ) {
      const timestamp =
        Number(expiresAt);

      if (
        !Number.isFinite(
          timestamp
        )
      ) {
        this.handleError(
          new Error(
            '[LimitedCartOffer] Invalid expiration timestamp.'
          )
        );

        return;
      }

      this.stopCountdown();

      this.expirationTimestamp =
        timestamp;

      const tick = () => {
        const remaining =
          this
            .updateCountdownDisplay();

        if (
          remaining <= 0 &&
          !this
            .expirationInProgress
        ) {
          this.expirationInProgress =
            true;

          this.stopCountdown();

          this.handleExpiration()
            .catch((error) => {
              this.handleError(
                error
              );
            })
            .finally(() => {
              this
                .expirationInProgress =
                false;
            });
        }
      };

      tick();

      this.countdownId =
        window.setInterval(
          tick,
          1000
        );
    }

    /**
     * Esta es la fuente principal
     * del estado visual.
     */
    applyStateFromLocalStorage(
      roots = null
    ) {
      const targetRoots =
        roots || this.getRoots();

      if (
        targetRoots.length === 0
      ) {
        return 'missing';
      }

      let claimedExpiration =
        this
          .getStoredClaimedExpiration();

      let expiration =
        this
          .getStoredExpiration();

      const now =
        Date.now();

      if (
        Number.isFinite(
          claimedExpiration
        ) &&
        claimedExpiration > now
      ) {
        this.claimed = true;

        this.expirationTimestamp =
          claimedExpiration;

        this.setOfferState(
          'claimed',
          targetRoots
        );

        this.setButtonState(
          'claimed',
          targetRoots
        );

        this.updateCountdownDisplay(
          targetRoots
        );

        if (
          this.countdownId ===
          null
        ) {
          this.startCountdown(
            claimedExpiration
          );
        }

        return 'claimed';
      }

      if (
        Number.isFinite(
          expiration
        ) &&
        expiration <= now
      ) {
        this.claimed = false;

        this.setOfferState(
          'expired',
          targetRoots
        );

        this.setButtonState(
          'expired',
          targetRoots
        );

        this.setTimerValue(
          0,
          targetRoots
        );

        return 'expired';
      }

      if (
        !Number.isFinite(
          expiration
        )
      ) {
        expiration =
          this.createExpiration();
      }

      this.claimed = false;

      this.expirationTimestamp =
        expiration;

      this.setOfferState(
        'available',
        targetRoots
      );

      this.setButtonState(
        'default',
        targetRoots
      );

      this.updateCountdownDisplay(
        targetRoots
      );

      if (
        this.countdownId ===
        null
      ) {
        this.startCountdown(
          expiration
        );
      }

      return 'available';
    }

    async handleExpiration() {
      this.stopCountdown();

      this.setTimerValue(0);

      this.expirationTimestamp =
        null;

      this.clearStoredExpiration();
      this.clearStoredClaimedExpiration();

      if (this.claimed) {
        try {
          await this
            .updateCartAttributes({
              [this.config
                .claimedAttribute]: ''
            });
        } catch (error) {
          this.handleError(
            error
          );
        }
      }

      this.claimed = false;

      this.setButtonState(
        'expired'
      );

      this.setOfferState(
        'expired'
      );

      const detail = {
        expired: true
      };

      this.emit(
        'limited-offer:expired',
        detail
      );

      if (
        typeof this.config
          .onExpired ===
        'function'
      ) {
        await this.config
          .onExpired(detail);
      }
    }

    async claim() {
      if (
        this.requestInProgress
      ) {
        return null;
      }

      if (
        !Number.isFinite(
          this
            .expirationTimestamp
        ) ||
        this
          .expirationTimestamp <=
          Date.now()
      ) {
        await this
          .handleExpiration();

        return {
          claimed: false,
          expired: true
        };
      }

      if (this.claimed) {
        this
          .applyStateFromLocalStorage();

        return {
          claimed: true,
          alreadyClaimed: true
        };
      }

      this.requestInProgress =
        true;

      this.setButtonState(
        'loading'
      );

      try {
        const expirationIso =
          new Date(
            this
              .expirationTimestamp
          ).toISOString();

        const attributes = {
          [this.config
            .claimedAttribute]:
            expirationIso
        };

        const cart =
          await this
            .updateCartAttributes(
              attributes
            );

        /*
         * Primero localStorage.
         * Después el HTML.
         */
        this.claimed = true;

        this.saveExpiration(
          this
            .expirationTimestamp
        );

        this
          .saveStoredClaimedExpiration(
            this
              .expirationTimestamp
          );

        this
          .applyStateFromLocalStorage();

        const detail = {
          claimed: true,
          alreadyClaimed: false,
          expiresAt:
            expirationIso,
          expiresAtTimestamp:
            this
              .expirationTimestamp,
          cart,
          attributes
        };

        this.emit(
          'limited-offer:claimed',
          detail
        );

        if (
          typeof this.config
            .onClaimed ===
          'function'
        ) {
          await this.config
            .onClaimed(detail);
        }

        return detail;
      } catch (error) {
        this.setButtonState(
          'error'
        );

        this.applyStateFromLocalStorage();

        this.handleError(error);

        throw error;
      } finally {
        this.requestInProgress =
          false;
      }
    }

    async remove() {
      if (
        this.requestInProgress
      ) {
        return null;
      }

      this.requestInProgress =
        true;

      try {
        const attributes = {
          [this.config
            .claimedAttribute]: ''
        };

        const cart =
          await this
            .updateCartAttributes(
              attributes
            );

        this.claimed = false;

        this
          .clearStoredClaimedExpiration();

        this
          .applyStateFromLocalStorage();

        const detail = {
          cart,
          attributes
        };

        this.emit(
          'limited-offer:removed',
          detail
        );

        if (
          typeof this.config
            .onRemoved ===
          'function'
        ) {
          await this.config
            .onRemoved(detail);
        }

        return cart;
      } catch (error) {
        this.handleError(error);
        throw error;
      } finally {
        this.requestInProgress =
          false;
      }
    }

    async restore() {
      /*
       * Render inmediato:
       * no espera cart.js.
       */
      const immediateState =
        this
          .applyStateFromLocalStorage();

      try {
        const cart =
          await this.fetchCart();

        const cartExpiration =
          this
            .getClaimedExpiration(
              cart
            );

        /*
         * Solo sincronizar hacia localStorage
         * si el carrito tiene un atributo vigente.
         *
         * No bajar visualmente de claimed a available
         * por una respuesta tardía o una mutación.
         */
        if (
          Number.isFinite(
            cartExpiration
          ) &&
          cartExpiration >
            Date.now()
        ) {
          this.saveExpiration(
            cartExpiration
          );

          this
            .saveStoredClaimedExpiration(
              cartExpiration
            );
        }

        const state =
          this
            .applyStateFromLocalStorage();

        const expiration =
          this
            .expirationTimestamp;

        const detail = {
          cart,
          claimed:
            state === 'claimed',
          expiresAt:
            Number.isFinite(
              expiration
            )
              ? new Date(
                  expiration
                ).toISOString()
              : null,
          expiresAtTimestamp:
            expiration
        };

        this.emit(
          'limited-offer:restored',
          detail
        );

        if (
          typeof this.config
            .onRestored ===
          'function'
        ) {
          await this.config
            .onRestored(detail);
        }

        return {
          status: state,
          ...detail
        };
      } catch (error) {
        /*
         * Si cart.js falla,
         * localStorage mantiene
         * el estado visual.
         */
        this.handleError(error);

        return {
          status:
            immediateState,
          claimed:
            immediateState ===
            'claimed',
          restoredFromLocalStorage:
            true
        };
      }
    }

    refresh() {
      window
        .requestAnimationFrame(
          () => {
            this
              .applyStateFromLocalStorage();
          }
        );
    }

    collectRootsFromNode(node) {
      const roots = [];

      if (
        !node ||
        node.nodeType !==
          Node.ELEMENT_NODE
      ) {
        return roots;
      }

      if (
        node.matches &&
        node.matches(
          this.config
            .rootSelector
        )
      ) {
        roots.push(node);
      }

      if (
        node.querySelectorAll
      ) {
        node
          .querySelectorAll(
            this.config
              .rootSelector
          )
          .forEach((root) => {
            roots.push(root);
          });
      }

      return roots;
    }

    handleMutations(mutations) {
      const roots = [];

      mutations.forEach(
        (mutation) => {
          mutation.addedNodes
            .forEach((node) => {
              roots.push(
                ...this
                  .collectRootsFromNode(
                    node
                  )
              );
            });
        }
      );

      if (
        roots.length === 0
      ) {
        return;
      }

      const uniqueRoots =
        Array.from(
          new Set(roots)
        );

      if (
        this.mutationFrame !==
        null
      ) {
        window
          .cancelAnimationFrame(
            this
              .mutationFrame
          );
      }

      this.mutationFrame =
        window
          .requestAnimationFrame(
            () => {
              this.mutationFrame =
                null;

              /*
               * Cada nodo nuevo se renderiza
               * directamente desde localStorage.
               */
              this
                .applyStateFromLocalStorage(
                  uniqueRoots
                );
            }
          );
    }

    handleClick(event) {
      if (
        !event.target ||
        typeof event.target
          .closest !==
          'function'
      ) {
        return;
      }

      const button =
        event.target.closest(
          this.config
            .buttonSelector
        );

      if (!button) {
        return;
      }

      event.preventDefault();

      const state =
        this
          .applyStateFromLocalStorage();

      if (
        state === 'claimed'
      ) {
        return;
      }

      this.claim()
        .catch(function () {
          /*
           * El error ya fue gestionado.
           */
        });
    }

    handleVisibilityChange() {
      if (
        document
          .visibilityState !==
          'visible'
      ) {
        return;
      }

      this
        .applyStateFromLocalStorage();
    }

    handleSectionLoad(event) {
      const section =
        event &&
        event.target
          ? event.target
          : document;

      let roots = [];

      if (
        section === document
      ) {
        roots =
          this.getRoots();
      } else {
        roots =
          this
            .collectRootsFromNode(
              section
            );
      }

      if (
        roots.length > 0
      ) {
        this
          .applyStateFromLocalStorage(
            roots
          );
      } else {
        this
          .applyStateFromLocalStorage();
      }
    }

    bindEvents() {
      document
        .removeEventListener(
          'click',
          this.handleClick,
          true
        );

      document
        .addEventListener(
          'click',
          this.handleClick,
          true
        );

      document
        .removeEventListener(
          'visibilitychange',
          this
            .handleVisibilityChange
        );

      document
        .addEventListener(
          'visibilitychange',
          this
            .handleVisibilityChange
        );

      document
        .removeEventListener(
          'shopify:section:load',
          this
            .handleSectionLoad
        );

      document
        .addEventListener(
          'shopify:section:load',
          this
            .handleSectionLoad
        );

      if (
        typeof MutationObserver !==
        'undefined'
      ) {
        if (this.observer) {
          this.observer
            .disconnect();
        }

        this.observer =
          new MutationObserver(
            this
              .handleMutations
          );

        this.observer.observe(
          document.body,
          {
            childList: true,
            subtree: true
          }
        );
      }
    }

    handleError(error) {
      const normalized =
        error instanceof Error
          ? error
          : new Error(
              String(error)
            );

      console.error(
        '[LimitedCartOffer]',
        normalized
      );

      this.emit(
        'limited-offer:error',
        {
          error: normalized
        }
      );

      if (
        typeof this.config
          .onError ===
        'function'
      ) {
        this.config
          .onError(
            normalized
          );
      }
    }

    async init(options) {
      this.config =
        this.mergeConfig(
          options || {}
        );

      if (!this.initialized) {
        this.bindEvents();

        this.initialized =
          true;
      }

      /*
       * Render inmediato desde
       * localStorage.
       */
      this
        .applyStateFromLocalStorage();

      /*
       * Sincronización posterior
       * con el carrito.
       */
      return this.restore();
    }

    destroy() {
      this.stopCountdown();

      document
        .removeEventListener(
          'click',
          this.handleClick,
          true
        );

      document
        .removeEventListener(
          'visibilitychange',
          this
            .handleVisibilityChange
        );

      document
        .removeEventListener(
          'shopify:section:load',
          this
            .handleSectionLoad
        );

      if (this.observer) {
        this.observer
          .disconnect();

        this.observer =
          null;
      }

      if (
        this.mutationFrame !==
        null
      ) {
        window
          .cancelAnimationFrame(
            this
              .mutationFrame
          );

        this.mutationFrame =
          null;
      }

      this.initialized =
        false;
    }
  }

  const instance =
    new LimitedCartOfferController();

  window.LimitedCartOffer = {
    init:
      instance.init.bind(
        instance
      ),

    claim:
      instance.claim.bind(
        instance
      ),

    remove:
      instance.remove.bind(
        instance
      ),

    restore:
      instance.restore.bind(
        instance
      ),

    refresh:
      instance.refresh.bind(
        instance
      ),

    destroy:
      instance.destroy.bind(
        instance
      ),

    fetchCart:
      instance.fetchCart.bind(
        instance
      ),

    updateCartAttributes:
      instance
        .updateCartAttributes
        .bind(instance),

    getCartUrl:
      instance.getCartUrl.bind(
        instance
      ),

    getConfig:
      function () {
        return {
          ...instance.config,

          texts: {
            ...instance
              .config.texts
          }
        };
      }
  };

  function initialize() {
    window
      .LimitedCartOffer
      .init({
        durationMinutes: 30
      })
      .catch((error) => {
        console.error(
          '[LimitedCartOffer] Initialization failed:',
          error
        );
      });
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document
      .addEventListener(
        'DOMContentLoaded',
        initialize,
        {
          once: true
        }
      );
  } else {
    initialize();
  }
})(window, document);

document.addEventListener(
  'DOMContentLoaded',
  function () {
    window.LimitedCartOffer.init({
      durationMinutes: 30,

      disableButtonWhenClaimed:
        false,

      hideOfferWhenClaimed:
        false,

      onCartUpdated:
        function (cart) {
          console.log(
            'Cart updated:',
            cart
          );
        },

      onClaimed:
        function (data) {
          console.log(
            'Discount applied:',
            data
          );
        },

      onExpired:
        function () {
          console.log(
            'Offer expired'
          );
        },

      onError:
        function (error) {
          console.error(
            'Offer error:',
            error
          );
        }
    });
  }
);